//userSlice
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Node,
  addEdge,
  Edge,
  Elements,
  removeElements,
  OnLoadParams,
  isEdge,
} from 'react-flow-renderer';
import { diagramApi } from '../../api';
import { ELEMENT_TYPE } from '../../constants/diagram';
import { DiagramSliceI, FetchDiagramI } from '../../types/diagram';

export const getDiagram = createAsyncThunk(
  'diagram/getDiagram',
  async (diagramId: string) => {
    const response = await diagramApi.fetchDiagram(diagramId);
    const data: FetchDiagramI = await response.data;
    return data;
  },
);

const initialState: DiagramSliceI = {
  id: '',
  diagram_name: '',
  diagram_description: '',
  diagram_category: '',
  diagram_software_type: '',
  created_at: '',
  updated_at: '',
  elements: [],
  is_diagram_public: false,
  selectedElement: null,
  reactFlowInstance: {} as OnLoadParams,
  showPropertiesForm: false,
  diagramErrors: {
    tables: [],
    columns: [],
  },
};

const diagramSlice = createSlice({
  name: 'diagram',
  initialState,
  reducers: {
    setDiagram: (state, action: PayloadAction<FetchDiagramI>) => {
      state.id = action.payload.id;
      state.diagram_name = action.payload.diagram_name;
      state.diagram_description = action.payload.diagram_description;
      state.diagram_category = action.payload.diagram_category;
      state.diagram_software_type = action.payload.diagram_software_type;
      state.created_at = action.payload.created_at;
      state.updated_at = action.payload.updated_at;
      state.elements = action.payload.elements;
      state.is_diagram_public = action.payload.is_diagram_public;
    },

    setReactFlowInstance: (state, action: PayloadAction<OnLoadParams>) => {
      state.reactFlowInstance = action.payload;
    },

    setDiagramElements: (state, action: PayloadAction<any>) => {
      state.elements = state.elements.concat(action.payload);
    },

    removeDiagramElements: (state, action: PayloadAction<Elements>) => {
      if (isEdge(action.payload[0])) {
        const edgeData = action.payload[0];
        if (edgeData.type === ELEMENT_TYPE.FOREIGN_EDGE) {
          const column = state.elements.find(
            (element) => element.id === edgeData.source,
          );
          if (column) {
            column.data.constraints.FOREIGN_KEY = false;
          }
        }
      }
      state.elements = removeElements(action.payload, state.elements);
    },

    setDiagramEdges: (state, action: PayloadAction<Edge>) => {
      state.elements = addEdge(action.payload, state.elements);

      if (action.payload.type === ELEMENT_TYPE.FOREIGN_EDGE) {
        const column = state.elements.find(
          (element) => element.id === action.payload.source,
        );
        if (column) {
          column.data.constraints.FOREIGN_KEY = true;
        }
      }
    },

    updateTableData: (state, action: PayloadAction<any>) => {
      const newTableData = action.payload;
      state.elements = state.elements.map((element) => {
        if (element.id === newTableData.id) {
          return newTableData;
        }
        return element;
      });
    },

    updateColumnData: (state, action: PayloadAction<any>) => {
      const newColumnData = action.payload;
      state.elements = state.elements.map((element) => {
        if (element.id === newColumnData.id) {
          return newColumnData;
        }
        return element;
      });
    },

    setSelectedElement: (state, action: PayloadAction<Node | Edge | null>) => {
      state.selectedElement = action.payload;
    },

    setShowPropertiesForm: (state, action: PayloadAction<boolean>) => {
      state.showPropertiesForm = action.payload;
    },

    setDiagramErrors: (state, action: PayloadAction<any>) => {
      state.diagramErrors = action.payload;
    },

    resetDiagram: (state) => {
      state.id = '';
      state.diagram_name = '';
      state.diagram_description = '';
      state.diagram_category = '';
      state.diagram_software_type = '';
      state.created_at = '';
      state.updated_at = '';
      state.elements = [];
      state.is_diagram_public = false;
      state.selectedElement = null;
      state.reactFlowInstance = {} as OnLoadParams;
      state.showPropertiesForm = false;
      state.diagramErrors = {
        tables: [],
        columns: [],
      };
    },
  },
  extraReducers: {
    [getDiagram.fulfilled.type]: (state, action) => {
      state.id = action.payload.id;
      state.diagram_name = action.payload.diagram_name;
      state.diagram_description = action.payload.diagram_description;
      state.diagram_category = action.payload.diagram_category;
      state.diagram_software_type = action.payload.diagram_software_type;
      state.created_at = action.payload.created_at;
      state.updated_at = action.payload.updated_at;
      state.elements = action.payload.elements;
      state.is_diagram_public = action.payload.is_diagram_public;
    },
    [getDiagram.rejected.type]: (state) => {
      state.id = '';
      state.diagram_name = '';
      state.diagram_description = '';
      state.diagram_category = '';
      state.diagram_software_type = '';
      state.created_at = '';
      state.updated_at = '';
      state.elements = [];
      state.is_diagram_public = false;
    },
  },
});

export const {
  removeDiagramElements,
  setDiagram,
  setDiagramEdges,
  setDiagramElements,
  setDiagramErrors,
  setReactFlowInstance,
  setSelectedElement,
  setShowPropertiesForm,
  resetDiagram,
  updateTableData,
  updateColumnData,
} = diagramSlice.actions;

export const selectDiagram = (state: any) => state.diagram;

export default diagramSlice.reducer;
